import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { promptQueryKeys } from '@/hooks/prompts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { SavedPrompt } from '@/types/library/SavedPrompt'

interface UseSavedPromptArgs<T> {
  promptId: UUID | ''
  select?: (data: SavedPrompt) => T
  /**
   * Refetch on every mount regardless of staleness. On-demand surfaces (e.g.
   * the chat playbook viewer) use this so agent writes/edits from moments
   * ago are never hidden behind the Infinity staleTime.
   */
  alwaysFresh?: boolean
}

const fetchSavedPrompt = async (
  promptId: string,
  accessToken: string | undefined | null,
): Promise<SavedPrompt> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const res: Response = await fetch(`${API_URL}/api/v1/prompts/${promptId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as unknown as SavedPrompt
}

export const usePrompt = <T = SavedPrompt>({
  promptId,
  select,
  alwaysFresh = false,
}: UseSavedPromptArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: SavedPrompt) => {
      return select ? select(data) : (data as unknown as T)
    },
    [select],
  )

  return useQuery({
    queryFn: () => fetchSavedPrompt(promptId, accessToken),
    queryKey: promptQueryKeys.prompt({ promptId }),
    staleTime: alwaysFresh ? 0 : Infinity,
    refetchOnMount: alwaysFresh ? 'always' : true,
    select: selectFn,
    enabled: !!promptId,
  })
}
